import type { UIMessage } from "@ai-sdk/react";
import { z } from "zod";
import type { FeedbackMap, MessageFeedback } from "./protocol";

export type ChatThread = {
  id: string;
  title: string | null;
  consultId: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ThreadMessagesResult = {
  messages: UIMessage[];
  feedback: FeedbackMap;
  // 回答を生成中か。回答は接続と切り離して生成されるため、リロード後も生成が続いていることがある。
  running: boolean;
};

/**
 * agent API への通信に必要な、配信面(env / 認証基盤 / ルート)依存の設定。
 * ChatConfigProvider が注入し、各リクエスト関数へ明示的に渡す(パッケージ側は env / Amplify を
 * 直接読まず、module state も持たない = SSR でリクエスト間に漏れない)。
 *
 * ストリームとスレッド系は BE で接頭辞の切れ目が揃っていない(医師 = "/chat" + "/chat/threads"、
 * 管理者 PE = "/pe/chat" + "/pe/threads")ため、2つの実体を別フィールドで持つ。
 */
export type AgentApiTransport = {
  // 例: web = NEXT_PUBLIC_AGENT_API_URL。値はパッケージが知らない。
  agentApiUrl: string;
  // ストリーム(POST)用パス。医師 = "/chat"、管理者 PE = "/pe/chat"。
  chatPath: string;
  // スレッド系(GET/POST/PATCH/DELETE)のベースパス。医師 = "/chat/threads"、管理者 PE = "/pe/threads"。
  threadsPath: string;
  // 医師 = Cognito idToken / 独立版 = registered プールの idToken を注入する。
  getAuthToken: () => Promise<string>;
};

async function authHeaders(
  transport: AgentApiTransport,
): Promise<Record<string, string>> {
  return { Authorization: `Bearer ${await transport.getAuthToken()}` };
}

function threadsBaseUrl(transport: AgentApiTransport): string {
  return `${transport.agentApiUrl}${transport.threadsPath}`;
}

/** consultId 指定時は、その診察に紐づくスレッドのみを取得する(診察ページのパネル用)。 */
export async function fetchThreads(
  transport: AgentApiTransport,
  consultId?: string,
): Promise<ChatThread[]> {
  const url = new URL(threadsBaseUrl(transport));
  if (consultId) {
    url.searchParams.set("consultId", consultId);
  }
  const res = await fetch(url, {
    headers: await authHeaders(transport),
  });
  if (!res.ok) {
    throw new Error("スレッド一覧の取得に失敗しました");
  }
  const data: { threads: ChatThread[] } = await res.json();
  return data.threads;
}

export async function fetchThreadMessages(
  transport: AgentApiTransport,
  threadId: string,
): Promise<ThreadMessagesResult> {
  const res = await fetch(`${threadsBaseUrl(transport)}/${threadId}/messages`, {
    headers: await authHeaders(transport),
  });
  if (!res.ok) {
    throw new Error("メッセージの取得に失敗しました");
  }
  const data: {
    messages: UIMessage[];
    feedback?: FeedbackMap;
    running?: boolean;
  } = await res.json();
  return {
    messages: data.messages,
    feedback: data.feedback ?? {},
    running: data.running ?? false,
  };
}

/**
 * 生成中の回答を停止する(停止ボタン)。回答はストリームを切断しても止まらないため、
 * 止めるにはこれを呼ぶ必要がある。
 */
export async function stopChatRun(
  transport: AgentApiTransport,
  threadId: string,
): Promise<void> {
  const res = await fetch(`${threadsBaseUrl(transport)}/${threadId}/stop`, {
    method: "POST",
    headers: await authHeaders(transport),
  });
  if (!res.ok) {
    throw new Error("回答を停止できませんでした");
  }
}

/** 回答フィードバック(👍/👎・👎理由)を upsert する。 */
export async function putMessageFeedback(
  transport: AgentApiTransport,
  threadId: string,
  messageId: string,
  feedback: MessageFeedback,
): Promise<void> {
  // BE は categories/comment を optional(未指定=null 扱い)で受ける。未選択の項目は
  // null を送らず省略する(状態としての null はそのまま保持し、送信時のみ落とす)。
  const body = {
    verdict: feedback.verdict,
    ...(feedback.categories && feedback.categories.length > 0
      ? { categories: feedback.categories }
      : {}),
    ...(feedback.comment ? { comment: feedback.comment } : {}),
  };
  const res = await fetch(
    `${threadsBaseUrl(transport)}/${threadId}/messages/${messageId}/feedback`,
    {
      method: "PUT",
      headers: {
        ...(await authHeaders(transport)),
        "content-type": "application/json",
      },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    throw new Error("フィードバックの保存に失敗しました");
  }
}

/** 回答フィードバックを取り消す(未評価に戻す・冪等)。 */
export async function deleteMessageFeedback(
  transport: AgentApiTransport,
  threadId: string,
  messageId: string,
): Promise<void> {
  const res = await fetch(
    `${threadsBaseUrl(transport)}/${threadId}/messages/${messageId}/feedback`,
    { method: "DELETE", headers: await authHeaders(transport) },
  );
  if (!res.ok) {
    throw new Error("フィードバックの取り消しに失敗しました");
  }
}

export async function deleteThread(
  transport: AgentApiTransport,
  threadId: string,
): Promise<void> {
  const res = await fetch(`${threadsBaseUrl(transport)}/${threadId}`, {
    method: "DELETE",
    headers: await authHeaders(transport),
  });
  if (!res.ok) {
    throw new Error("スレッドの削除に失敗しました");
  }
}

export async function renameThread(
  transport: AgentApiTransport,
  threadId: string,
  title: string,
): Promise<void> {
  const res = await fetch(`${threadsBaseUrl(transport)}/${threadId}`, {
    method: "PATCH",
    headers: {
      ...(await authHeaders(transport)),
      "content-type": "application/json",
    },
    body: JSON.stringify({ title }),
  });
  if (!res.ok) {
    throw new Error("スレッド名の変更に失敗しました");
  }
}

const documentViewResponseSchema = z.object({
  url: z.string().url(),
  expiresIn: z.number().positive(),
});

/** 出典の相対パスは backend の documentViewUrlPath が生成する。 */
export async function fetchPersonalDocumentViewUrl(
  transport: AgentApiTransport,
  reference: { url: string; documentId: string; page?: number },
): Promise<string> {
  if (
    !/^\/chat\/documents\/[0-9a-f-]{36}\/view-url$/i.test(reference.url) ||
    reference.url.split("/")[3] !== reference.documentId
  ) {
    throw new Error("資料の参照先が不正です");
  }
  const response = await fetch(`${transport.agentApiUrl}${reference.url}`, {
    headers: await authHeaders(transport),
  });
  if (!response.ok)
    throw new Error("資料を開けませんでした。削除済みか、利用できない資料です");
  const data = documentViewResponseSchema.parse(await response.json());
  const url = new URL(data.url);
  if (url.protocol !== "https:" && url.protocol !== "http:")
    throw new Error("資料の参照先が不正です");
  if (reference.page) url.hash = `page=${reference.page}`;
  return url.toString();
}
